/**
 * @file Keeps the sidebar's Messages badge fresh (host-messaging-tech-design
 * §6.3, ui-design §3.15), the way useHostInboxBadge keeps the Inbox's.
 *
 * No poller of its own: every host reply writes a `guest_message` bell item,
 * and every booking change a `guest_booking` one, so a rise in the bell's
 * polled count means the badge may have changed too.
 * `GET user/conversations/summary` is refetched:
 *
 *  - when a signed-in member's app mounts;
 *  - when the window regains focus;
 *  - whenever the bell's polled `unreadCount` rises.
 *
 * Every signed-in account can be someone's guest, travel hosts included (R6),
 * so it runs for all of them. A failed fetch keeps the last values.
 *
 * Plain .js: no JSX.
 */
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchConversationSummary } from 'store/features/conversationSlice';

const selectSignedIn = (state) => Boolean(state.user?.isLoggedIn);

export const useGuestMessagesBadge = () => {
  const dispatch = useDispatch();
  const signedIn = useSelector(selectSignedIn);
  const unreadCount = useSelector((state) => state.notification?.unreadCount ?? 0);
  const prevUnreadRef = useRef(unreadCount);

  useEffect(() => {
    if (!signedIn) return undefined;
    const refresh = () => dispatch(fetchConversationSummary());
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [dispatch, signedIn]);

  useEffect(() => {
    const previous = prevUnreadRef.current;
    prevUnreadRef.current = unreadCount;
    if (signedIn && unreadCount > previous) dispatch(fetchConversationSummary());
  }, [dispatch, signedIn, unreadCount]);
};

export default useGuestMessagesBadge;
